import { useState } from "react";
import { PatientNameFields, PatientStatusFields, usePatientIdentityForm } from "../components/PatientIdentityParts";
import { formatFollowUpPatient } from "../model/followUpRules";
import {
    confirmDeleteButtonStyle,
    contactDetailsStyle,
    dangerButtonStyle,
    errorStyle,
    formSectionStyle,
    formSectionTitleStyle,
    modalOverlayStyle,
    modalStyle,
    primaryButtonStyle,
    secondaryButtonStyle,
} from "../styles/materielStyles";

function ContactDetail({ label, value }) {
    return (
        <div>
            <dt style={{ color: "#64748b", fontSize: "10px", fontWeight: 800, textTransform: "uppercase" }}>{label}</dt>
            <dd style={{ margin: "3px 0 0", color: "#0f172a", fontSize: "13px", overflowWrap: "anywhere" }}>{value || "—"}</dd>
        </div>
    );
}

function PatientContactsSummary({ contacts }) {
    return (
        <section style={{ ...formSectionStyle, padding: 0, border: 0, borderTop: "1px solid #e2e8f0", borderRadius: 0, paddingTop: "14px" }}>
            <h3 style={formSectionTitleStyle}>Informations de contact</h3>
            {contacts.length === 0 ? (
                <p style={{ margin: 0, color: "#64748b", fontSize: "13px" }}>Aucun contact renseigné pour ce patient.</p>
            ) : contacts.map(contact => (
                <dl key={contact.id} style={contactDetailsStyle}>
                    <ContactDetail label="DCD" value={contact.dcd} />
                    <ContactDetail label="Matériel de location" value={contact.rentalEquipment} />
                    <ContactDetail label="Téléphone" value={(contact.phoneNumbers || []).join(" · ")} />
                    <ContactDetail label="Crédit" value={contact.credit} />
                    <ContactDetail label="Date d'envoi" value={contact.sentDate} />
                    <ContactDetail label="Remarque" value={contact.remark} />
                </dl>
            ))}
        </section>
    );
}

function DeleteFollowUpPatientDialog({ row, deleting, onCancel, onConfirm }) {
    return (
        <div role="presentation" style={{ ...modalOverlayStyle, zIndex: 1001 }} onMouseDown={() => !deleting && onCancel()}>
            <div role="alertdialog" aria-modal="true" aria-labelledby="delete-follow-up-title" style={modalStyle} onMouseDown={event => event.stopPropagation()}>
                <h2 id="delete-follow-up-title" style={{ margin: 0, fontSize: "18px", color: "#111827" }}>Supprimer ce patient ?</h2>
                <p style={{ margin: 0, color: "#475569", lineHeight: 1.5 }}>
                    La ligne de suivi de {formatFollowUpPatient(row) || "ce patient"} sera définitivement supprimée.
                </p>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                    <button type="button" disabled={deleting} onClick={onCancel} style={secondaryButtonStyle}>Annuler</button>
                    <button type="button" disabled={deleting} onClick={onConfirm} style={confirmDeleteButtonStyle}>
                        {deleting ? "Suppression..." : "Confirmer la suppression"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Identité du patient d'une ligne de suivi, avec ses contacts en lecture et la suppression de la ligne.
export function FollowUpPatientModal({ row, contacts = [], saving, onClose, onSave, onDelete }) {
    const identity = usePatientIdentityForm({ ...row, nom: row.patientNom, prenom: row.patientPrenom }, patch => onSave({
        patientNom: patch.nom,
        patientPrenom: patch.prenom,
        patient: patch.patient,
        notInvoiced: patch.notInvoiced,
        inactive: patch.inactive,
    }));
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const confirmDeletion = async () => {
        setDeleting(true);
        await onDelete();
        setDeleting(false);
    };

    return (
        <>
            <div role="presentation" style={modalOverlayStyle} onMouseDown={onClose}>
                <div role="dialog" aria-modal="true" aria-labelledby="follow-up-patient-modal-title" style={modalStyle} onMouseDown={event => event.stopPropagation()}>
                    <h2 id="follow-up-patient-modal-title" style={{ margin: 0, fontSize: "18px", color: "#111827" }}>Modifier le patient</h2>
                    <form onSubmit={identity.submit} style={{ display: "grid", gap: "14px" }}>
                        {identity.error && <div role="alert" style={errorStyle}>{identity.error}</div>}
                        <PatientNameFields form={identity.form} setField={identity.setField} />
                        <div style={{ display: "grid", gap: "12px" }}>
                            <PatientStatusFields form={identity.form} setField={identity.setField} />
                        </div>
                        <PatientContactsSummary contacts={contacts} />
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
                            <button type="button" onClick={() => setConfirmDelete(true)} disabled={saving} style={dangerButtonStyle}>Supprimer le patient</button>
                            <div style={{ display: "flex", gap: "10px" }}>
                                <button type="button" onClick={onClose} disabled={saving} style={secondaryButtonStyle}>Annuler</button>
                                <button type="submit" disabled={saving} style={primaryButtonStyle}>{saving ? "Enregistrement..." : "Enregistrer"}</button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
            {confirmDelete && (
                <DeleteFollowUpPatientDialog row={row} deleting={deleting} onCancel={() => setConfirmDelete(false)} onConfirm={confirmDeletion} />
            )}
        </>
    );
}
